"use client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Check, Linkedin } from "lucide-react";
import { LightBulbIcon } from "./Icons";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import Link from "next/link";
import { useTranslation } from "@/hooks/useLanguage";

export const HeroCards = () => {
  const { t } = useTranslation();
  const benefits = t("home.hero.cards.card_3_benefits");
  // Ensure benefits is always an array to prevent runtime errors
  const safeBenefits = Array.isArray(benefits) ? benefits : [];

  return (
    <div className="hidden lg:flex flex-row flex-wrap gap-8 relative w-[700px] h-[500px]">
      {/* Testimonial */}
      <Card className="absolute w-[340px] -top-[15px] drop-shadow-xl shadow-black/10 dark:shadow-white/10">
        <CardHeader className="flex flex-row items-center gap-4 pb-2">
          <Avatar>
            <AvatarImage alt="user" src="https://i.pravatar.cc/150?img=65" />
            <AvatarFallback>AM</AvatarFallback>
          </Avatar>

          <div className="flex flex-col">
            <CardTitle className="text-lg">
              {t("home.hero.cards.card_1_title")}
            </CardTitle>
            <CardDescription>
              {t("common.hotel_owner")}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>{t("home.hero.cards.card_1_testimonial")}</CardContent>
      </Card>

      {/* Team */}
      <Card className="absolute end-[20px] top-4 w-80 flex flex-col justify-center items-center drop-shadow-xl shadow-black/10 dark:shadow-white/10">
        <CardHeader className="mt-8 flex justify-center items-center pb-2">
          <img
            src="https://i.pravatar.cc/150?img=58"
            alt="user avatar"
            className="absolute grayscale-0 -top-12 rounded-full w-24 h-24 aspect-square object-cover"
          />
          <CardTitle className="text-center">
            {t("home.hero.cards.card_2_title")}
          </CardTitle>
          <CardDescription className="font-normal text-primary">
            {t("common.hotel_owner")}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-center pb-2">
          <p>{t("home.hero.cards.card_1_testimonial")}</p>
        </CardContent>

        <CardFooter>
          <div>
            <Link
              rel="noreferrer noopener"
              href="https://github.com/"
              target="_blank"
              className={buttonVariants({
                variant: "ghost",
                size: "sm",
              })}
            >
              <GitHubLogoIcon className="w-5 h-5" />
            </Link>
            <Link
              rel="noreferrer noopener"
              href="https://www.linkedin.com/"
              target="_blank"
              className={buttonVariants({
                variant: "ghost",
                size: "sm",
              })}
            >
              <Linkedin size="20" />
            </Link>
          </div>
        </CardFooter>
      </Card>

      {/* Pricing */}
      <Card className="absolute top-[150px] start-[50px] w-72 drop-shadow-xl shadow-black/10 dark:shadow-white/10">
        <CardHeader>
          <CardTitle className="flex item-center justify-between">
            {t("common.john_doe")}
            <Badge variant="secondary" className="text-sm text-primary">
              {t("common.hotel_owner")}
            </Badge>
          </CardTitle>
          <div>
            <span className="text-3xl font-bold">
              {t("home.hero.cards.card_3_price")}
            </span>
            <span className="text-muted-foreground">
              {t("home.hero.cards.card_3_billing_period")}
            </span>
          </div>

          <CardDescription>
            {t("home.hero.cards.card_1_testimonial")}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Button asChild className="w-full">
            <Link href="/register">{t("home.hero.cards.card_3_button")}</Link>
          </Button>
        </CardContent>

        <hr className="w-4/5 m-auto mb-4" />

        <CardFooter className="flex">
          <div className="space-y-4">
            {safeBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center">
                <Check className="text-green-500" />
                <p className="ms-2">{benefit}</p>
              </div>
            ))}
          </div>
        </CardFooter>
      </Card>

      {/* Service */}
      <Card className="absolute w-[350px] end-[-10px] bottom-[35px] drop-shadow-xl shadow-black/10 dark:shadow-white/10">
        <CardHeader className="space-y-1 flex md:flex-row justify-start items-start gap-4">
          <div className="mt-1 bg-primary/20 p-1 rounded-2xl">
            <LightBulbIcon />
          </div>
          <div>
            <CardTitle>{t("home.hero.cards.service_title")}</CardTitle>
            <CardDescription className="text-md mt-2">
              {t("home.hero.cards.service_description")}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
};
